import { Component, inject, Input, OnInit, QueryList, ViewChildren, ViewEncapsulation } from '@angular/core';
import { AgreementModel } from '@fi-shared/fi-agreement/agreement.model';
import { ContentLanguagesService } from '@fi-app/utilities/fi-services/content-languages.service';
import { AgreementService } from '../agreement.service';
import { LoadingService } from '@fi-shared/fi-loading/loading.service';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { SaveChangesWarningService } from '@fi-shared/fi-shared-services/save-changes-warning.service';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { AppTextService } from '@fi-shared/app-text';
import { AgreementPublishedContentModel } from '@fi-shared/fi-agreement/agreement-published-content.model';
import { DropdownListSettingsLanguageModel } from '@fi-shared/dropdown';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { EditTabsModel } from '@fi-app/utilities/edit-tabs/edit-tabs.model';
import { IDropdownSelectLanguageSettingsItem } from '@fi-shared/fi-content-language-shared/fi-content-language-shared';
import { ObjectTypes } from '@fi-shared/types';
import { isNullOrUndefined } from '@fi-app/shared/utils/is-null-or-undefined';

@Component({
    selector: 'fi-agreement-edit',
    templateUrl: './agreement-edit.component.html',
    styleUrls: ['./agreement-edit.component.scss'],
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-agreement-edit' },
    standalone: false,
})
export class FiAgreementEditComponent implements OnInit {
    @Input() languageId: number;
    @Input() agreement: AgreementModel;
    protected languageListData: DropdownListSettingsLanguageModel<IDropdownSelectLanguageSettingsItem>;

    private currentPublishedContent = null; // ? remove?

    protected selectedLanguageId: number;

    private contentLanguages: ContentLanguagesService = inject(ContentLanguagesService);
    private agreementService: AgreementService = inject(AgreementService);
    private loadingService: LoadingService = inject(LoadingService);
    private stateService: FiStateService = inject(FiStateService);
    private saveChangesWarningService: SaveChangesWarningService = inject(SaveChangesWarningService);
    private fiDialogsService: FiDialogsService = inject(FiDialogsService);
    private appTextService: AppTextService = inject(AppTextService);

    protected EditTabs: EditTabsModel;

    logger() {}

    public ngOnInit(): void {
        this.EditTabs = new EditTabsModel(this.getAgreementName(), this.appTextService.get('common.cancel'));
        this.setLanguageListData();
        this.saveChangesWarningService.start(() => {
            return { agreement: this.agreement };
        });
    }

    private setLanguageListData(): void {
        this.selectedLanguageId = this.languageId || this.contentLanguages.GetDefaultLanguageId();
        this.languageListData = this.contentLanguages.GetDropdownListSettingsLanguage();

        // fill agreements with content if some content doesn't exists
        this.languageListData.dataSet.forEach((item) => {
            const existingContent = this.agreement.AgreementPublishedContents.find(
                (content) => content.ContentLanguageId == item.Id,
            );
            if (isNullOrUndefined(existingContent)) {
                this.agreement.AgreementPublishedContents.push(
                    this.agreementService.getAgreementPublishedContentModelInstance({
                        AgreementId: this.agreement.Id,
                        ContentLanguageId: item.Id,
                    }),
                );
            }
        });
    }

    protected isLanguageSelected(languageId: number): boolean {
        return this.selectedLanguageId === languageId;
    }

    protected save(): void {
        if (this.isRequiredContentEmpty()) {
            this.showMissingContentError();
            return;
        }

        // we need to wait for images to loose their edit-classes to be saved before saving the agreement
        setTimeout(() => {
            this.loadingService.trackPromise(this.agreementService.SaveAgreement(this.agreement)).then(() => {
                this.saveChangesWarningService.stop();
                this.stateService.go(FiStateNames.Agreement.View._name, { Id: this.agreement.Id });
            });
        });
    }

    private isRequiredContentEmpty(): boolean {
        const defaultContent: AgreementPublishedContentModel = this.agreement.AgreementPublishedContents.find(
            (content) => this.isDefaultContent(content),
        );
        return defaultContent.Content.trim() === '';
    }

    protected showMissingContentError(): void {
        this.fiDialogsService.notify({
            messageText: this.appTextService.get('common.error_all_mandatory'),
            headerText: this.appTextService.get('common.warning'),
        });
    }

    private isDefaultContent(agreementContent: AgreementPublishedContentModel): boolean {
        return agreementContent.ContentLanguageId === this.languageListData.defaultLanguageId;
    }

    private getAgreementName(): string {
        return this.agreementService.getAgreementName(this.agreement);
    }

    protected readonly ObjectTypes = ObjectTypes;
}
